import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreateFuncionarioData, Funcionario } from "@/hooks/useFuncionarios";
import { useDentistas } from "@/hooks/useDentistas";

interface FuncionarioFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFuncionarioData) => Promise<boolean>;
  funcionario?: Funcionario | null;
  title: string;
}

export function FuncionarioForm({ isOpen, onClose, onSubmit, funcionario, title }: FuncionarioFormProps) {
  const { dentistas } = useDentistas();
  const [formData, setFormData] = useState<CreateFuncionarioData>({
    nome: funcionario?.nome || "",
    cargo: funcionario?.cargo || "",
    telefone: funcionario?.telefone || "",
    email: funcionario?.email || "",
    data_admissao: funcionario?.data_admissao || "",
    recebe_meta: funcionario?.recebe_meta || false,
    meta_periodo: funcionario?.meta_periodo || "Mensal",
    meta_inicio: funcionario?.meta_inicio || "",
    meta_fim: funcionario?.meta_fim || "",
    meta_valor: Number(funcionario?.meta_valor || 0),
    meta_bonus: Number(funcionario?.meta_bonus || 0),
    salario_base: Number(funcionario?.salario_base || 0),
    recebe_comissao: funcionario?.recebe_comissao || false,
    percentual_comissao: Number(funcionario?.percentual_comissao || 0),
    base_comissao: funcionario?.base_comissao || "unidade",
    dentista_id_comissao: funcionario?.dentista_id_comissao || "",
    status: funcionario?.status || "Ativo",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Atualiza o formulário quando o funcionário mudar
  useEffect(() => {
    if (funcionario) {
      setFormData({
        nome: funcionario.nome || "",
        cargo: funcionario.cargo || "",
        telefone: funcionario.telefone || "",
        email: funcionario.email || "",
        data_admissao: funcionario.data_admissao || "",
        recebe_meta: funcionario.recebe_meta || false,
        meta_periodo: funcionario.meta_periodo || "Mensal",
        meta_inicio: funcionario.meta_inicio || "",
        meta_fim: funcionario.meta_fim || "",
        meta_valor: Number(funcionario.meta_valor || 0),
        meta_bonus: Number(funcionario.meta_bonus || 0),
        salario_base: Number(funcionario.salario_base || 0),
        recebe_comissao: funcionario.recebe_comissao || false,
        percentual_comissao: Number(funcionario.percentual_comissao || 0),
        base_comissao: funcionario.base_comissao || "unidade",
        dentista_id_comissao: funcionario.dentista_id_comissao || "",
        status: funcionario.status || "Ativo",
      });
    } else {
      setFormData({
        nome: "",
        cargo: "",
        telefone: "",
        email: "",
        data_admissao: "",
        recebe_meta: false,
        meta_periodo: "Mensal",
        meta_inicio: "",
        meta_fim: "",
        meta_valor: 0,
        meta_bonus: 0,
        salario_base: 0,
        recebe_comissao: false,
        percentual_comissao: 0,
        base_comissao: "unidade",
        dentista_id_comissao: "",
        status: "Ativo",
      });
    }
  }, [funcionario]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const success = await onSubmit(formData);
    
    setIsSubmitting(false);
    
    if (success) {
      onClose();
      setFormData({
        nome: "",
        cargo: "",
        telefone: "",
        email: "",
        data_admissao: "",
        recebe_meta: false,
        meta_periodo: "Mensal",
        meta_inicio: "",
        meta_fim: "",
        meta_valor: 0,
        meta_bonus: 0,
        salario_base: 0,
        recebe_comissao: false,
        percentual_comissao: 0,
        base_comissao: "unidade",
        dentista_id_comissao: "",
        status: "Ativo",
      });
    }
  };

  const handleChange = (field: keyof CreateFuncionarioData, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => handleChange("nome", e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="cargo">Cargo *</Label>
              <Input
                id="cargo"
                value={formData.cargo}
                onChange={(e) => handleChange("cargo", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="telefone">Telefone *</Label>
              <Input
                id="telefone"
                value={formData.telefone}
                onChange={(e) => handleChange("telefone", e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="data_admissao">Data de Admissão</Label>
              <Input
                id="data_admissao"
                type="date"
                value={formData.data_admissao}
                onChange={(e) => handleChange("data_admissao", e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <h3 className="font-medium">Meta</h3>
              <p className="text-sm text-muted-foreground">Configure alvo, prazo e bônus de meta deste funcionário.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="recebe_meta">Recebe meta?</Label>
                <Select value={formData.recebe_meta ? "sim" : "nao"} onValueChange={(value) => handleChange("recebe_meta", value === "sim")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao">Não</SelectItem>
                    <SelectItem value="sim">Sim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="meta_periodo">Período</Label>
                <Select value={formData.meta_periodo || "Mensal"} onValueChange={(value) => handleChange("meta_periodo", value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Diária">Diária</SelectItem>
                    <SelectItem value="Semanal">Semanal</SelectItem>
                    <SelectItem value="Mensal">Mensal</SelectItem>
                    <SelectItem value="Personalizada">Personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="meta_inicio">Início</Label>
                <Input id="meta_inicio" type="date" value={formData.meta_inicio || ""} onChange={(e) => handleChange("meta_inicio", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="meta_fim">Fim</Label>
                <Input id="meta_fim" type="date" value={formData.meta_fim || ""} onChange={(e) => handleChange("meta_fim", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="meta_valor">Valor da meta (R$)</Label>
                <MoneyInput id="meta_valor" value={formData.meta_valor || 0} onValueChange={(meta_valor) => handleChange("meta_valor", meta_valor)} />
              </div>
              <div>
                <Label htmlFor="meta_bonus">Bônus ao bater meta (R$)</Label>
                <MoneyInput id="meta_bonus" value={formData.meta_bonus || 0} onValueChange={(meta_bonus) => handleChange("meta_bonus", meta_bonus)} />
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <h3 className="font-medium">Pagamento e comissão</h3>
              <p className="text-sm text-muted-foreground">Configure salário base e percentual de comissão para o fechamento de pagamento.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="salario_base">Salário base (R$)</Label>
                <MoneyInput id="salario_base" value={formData.salario_base || 0} onValueChange={(salario_base) => handleChange("salario_base", salario_base)} />
              </div>
              <div>
                <Label htmlFor="recebe_comissao">Recebe comissão?</Label>
                <Select value={formData.recebe_comissao ? "sim" : "nao"} onValueChange={(value) => handleChange("recebe_comissao", value === "sim")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao">Não</SelectItem>
                    <SelectItem value="sim">Sim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="percentual_comissao">Comissão (%)</Label>
                <Input id="percentual_comissao" type="number" min="0" max="100" step="0.01" value={formData.percentual_comissao || 0} onChange={(e) => handleChange("percentual_comissao", Number(e.target.value || 0))} />
              </div>
              <div>
                <Label htmlFor="base_comissao">Base de cálculo</Label>
                <Select value={formData.base_comissao || "unidade"} onValueChange={(value: "unidade" | "producao_pessoal") => handleChange("base_comissao", value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unidade">Produção da unidade</SelectItem>
                    <SelectItem value="producao_pessoal">Produção própria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.base_comissao === "producao_pessoal" && (
                <div className="sm:col-span-2">
                  <Label htmlFor="dentista_id_comissao">Dentista vinculado</Label>
                  <Select value={formData.dentista_id_comissao || "nenhum"} onValueChange={(value) => handleChange("dentista_id_comissao", value === "nenhum" ? "" : value)}>
                    <SelectTrigger><SelectValue placeholder="Selecione o dentista" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhum">Selecione o dentista</SelectItem>
                      {dentistas.map((dentista) => <SelectItem key={dentista.id} value={dentista.id}>{dentista.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">Use esta opção somente quando o funcionário recebe pela produção de um dentista específico.</p>
                </div>
              )}
            </div>
            {formData.recebe_comissao && (
              <p className="text-xs text-muted-foreground">
                A comissão será calculada automaticamente no fechamento, usando a base escolhida e somente serviços realizados ou ordens concluídas no período.
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
